import copy from '../../../copy/en/common.json' with { type: 'json' };
import { Tabs as RadixTabs } from "radix-ui";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router";
import { cx } from "./cx.js";

export interface TabItem {
  value: string;
  label: ReactNode;
  content: ReactNode;
  disabled?: boolean;
}

/** In-page tabs (state, not routes). */
export function Tabs({
  items,
  defaultValue,
  value,
  onValueChange,
  ariaLabel = copy.Tabs.sections,
  keepMounted = false,
}: {
  items: TabItem[];
  defaultValue?: string;
  value?: string;
  onValueChange?: (v: string) => void;
  ariaLabel?: string;
  /** Preserve inactive panel state and pending drafts in the DOM. */
  keepMounted?: boolean;
}) {
  const [internalValue, setInternalValue] = useState(defaultValue ?? items[0]?.value);
  const selected = value ?? internalValue;
  const listRef = useRevealActive(selected, '[data-state="active"]');
  return (
    <RadixTabs.Root value={selected} onValueChange={(next) => { setInternalValue(next); onValueChange?.(next); }}>
      <RadixTabs.List ref={listRef} className="tabs__list" aria-label={ariaLabel}>
        {items.map((t) => (
          <RadixTabs.Trigger key={t.value} value={t.value} className="tabs__trigger" disabled={t.disabled}>
            {t.label}
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>
      {items.map((t) => (
        <RadixTabs.Content key={t.value} value={t.value} className="tabs__content" forceMount={keepMounted || undefined}>
          {t.content}
        </RadixTabs.Content>
      ))}
    </RadixTabs.Root>
  );
}

export interface SubNavItem {
  to: string;
  label: ReactNode;
  /** Small muted hint (e.g. "interim", "Phase 1"). */
  hint?: ReactNode;
  end?: boolean;
}

/** Route-driven sub-navigation styled like tabs. */
export function SubNav({ items, ariaLabel }: { items: SubNavItem[]; ariaLabel: string }) {
  const { pathname } = useLocation();
  const listRef = useRevealActive(pathname, '[aria-current="page"]');
  return (
    <nav ref={listRef} className="subnav" aria-label={ariaLabel}>
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          className={({ isActive }) => cx("subnav__link", isActive && "is-active")}
        >
          {it.label}
          {it.hint ? <span className="subnav__hint">{it.hint}</span> : null}
        </NavLink>
      ))}
    </nav>
  );
}

/** Reveal only the horizontal selection; never move the page vertically. */
function useRevealActive(key: string | undefined, selector: string) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const list = ref.current;
    if (!list) return;
    const reveal = () => {
      const active = list.querySelector<HTMLElement>(selector);
      if (!active) return;
      const bounds = list.getBoundingClientRect();
      const item = active.getBoundingClientRect();
      if (item.left < bounds.left) list.scrollLeft += item.left - bounds.left;
      else if (item.right > bounds.right) list.scrollLeft += item.right - bounds.right;
    };
    reveal();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(reveal);
    observer.observe(list);
    return () => observer.disconnect();
  }, [key, selector]);
  return ref;
}
